import { cva } from "class-variance-authority";
import { Checkbox } from "@/components/ui";
import type { SignupChoices } from "@/lib/signup-choices";
import { useT } from "@/i18n";
import { signupMessages } from "@/i18n/messages/signup";

type Variant = "first-run" | "auth";

const rowVariants = cva("flex items-start gap-2.5", {
  variants: {
    variant: {
      "first-run": "col-span-full",
      auth: "max-w-[330px] text-left",
    },
  },
});
const boxVariants = cva("", {
  variants: { variant: { "first-run": "mt-px", auth: "mt-0.5" } },
});
const textVariants = cva("", {
  variants: {
    variant: {
      "first-run": "",
      auth: "text-appkit-caption leading-[1.45] text-[color:var(--auth-ink)]",
    },
  },
});
const hintVariants = cva("block", {
  variants: {
    variant: {
      "first-run": "text-appkit-small text-muted",
      auth: "text-[color:var(--auth-ink-secondary)]",
    },
  },
});

type SignupChoiceFieldsProps = {
  choices: SignupChoices;
  onChange: (choices: SignupChoices) => void;
  disabled?: boolean;
  /** Namespaces the element ids and test ids, e.g. "register". */
  prefix: string;
  variant: Variant;
};

/** The unticked newsletter opt-in shown on the sign-up screens. */
export function SignupChoiceFields({
  choices,
  onChange,
  disabled,
  prefix,
  variant,
}: SignupChoiceFieldsProps) {
  const t = useT(signupMessages);
  const boxId = `${prefix}-product-emails`;
  return (
    <div className={rowVariants({ variant })}>
      <Checkbox
        id={boxId}
        checked={choices.newsletterConsent}
        onCheckedChange={(checked) =>
          onChange({ ...choices, newsletterConsent: checked })
        }
        disabled={disabled}
        aria-describedby={`${boxId}-hint`}
        className={boxVariants({ variant })}
        data-testid={boxId}
      />
      <span className={textVariants({ variant })}>
        <label htmlFor={boxId} className="block">
          {t("productEmailsLabel")}
        </label>
        <span id={`${boxId}-hint`} className={hintVariants({ variant })}>
          {t("productEmailsHint")}
        </span>
      </span>
    </div>
  );
}
